import { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Printer, ChevronRight, ChevronDown,
  ShieldCheck, FileText, Lock, Bot, LifeBuoy, HeartPulse, Users,
  CreditCard, Cookie, Handshake, Accessibility as A11yIcon,
} from 'lucide-react';
import { TRUST_SAFETY_PAGES, findPage } from '@/data/trustSafetyContent';

/** Icon mapping — lucide-react icons keyed by content-config `icon` string. */
const ICONS = {
  ShieldCheck, FileText, Lock, Bot, LifeBuoy, HeartPulse, Users,
  CreditCard, Cookie, HandshakeIcon: Handshake, Accessibility: A11yIcon,
};

export default function TrustSafety() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const activeId = params.get('section') || 'terms';
  const active = useMemo(() => findPage(activeId), [activeId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TRUST_SAFETY_PAGES;
    return TRUST_SAFETY_PAGES.filter(p =>
      p.title.toLowerCase().includes(q) ||
      p.summary.toLowerCase().includes(q) ||
      p.sections.some(s => s.heading.toLowerCase().includes(q))
    );
  }, [query]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [activeId]);

  const goTo = (id) => setParams({ section: id });

  return (
    <div className="p-4 lg:p-8 lg:max-w-6xl lg:mx-auto space-y-6" data-testid="trust-safety-center">
      {/* Breadcrumb + title */}
      <div>
        <button type="button" onClick={() => navigate('/app/profile')}
                data-testid="trust-safety-back"
                className="inline-flex items-center gap-1.5 text-xs text-navy/60 hover:text-navy transition-smooth">
          <ArrowLeft size={13}/> Back to Profile
        </button>
        <div className="mt-3 bg-navy text-white rounded-3xl p-6 lg:p-8 card-soft">
          <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">Legal & Privacy</p>
          <h1 className="font-heading text-3xl lg:text-4xl mt-2">Trust &amp; Safety Center</h1>
          <p className="text-white/70 text-sm lg:text-base mt-2 max-w-xl">
            Everything you need to know about how Brotherhood+ protects your privacy, keeps its AI in check,
            and shows up as a wellness tool — not a substitute for professional care.
          </p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Sidebar navigation */}
        <aside className="lg:sticky lg:top-4 lg:self-start" data-testid="trust-safety-nav">
          <div className="bg-white rounded-3xl card-soft border border-navy/5 p-3">
            {/* Search */}
            <label className="relative block px-2 pt-1">
              <Search size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40"/>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                data-testid="trust-safety-search"
                className="w-full pl-8 pr-3 py-2 rounded-full bg-beige/60 border border-transparent focus:border-navy/20 focus:outline-none text-sm placeholder:text-navy/40"
              />
            </label>
            <nav className="mt-2">
              {filtered.length === 0 && (
                <p className="text-xs text-navy/45 p-3">No sections match &ldquo;{query}&rdquo;.</p>
              )}
              {filtered.map(p => {
                const Icon = ICONS[p.icon] || FileText;
                const isActive = p.id === activeId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => goTo(p.id)}
                    data-testid={`trust-safety-link-${p.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-smooth ${
                      isActive
                        ? 'bg-navy text-white'
                        : 'text-navy/80 hover:bg-beige/70'
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                      isActive ? 'bg-white/10' : 'bg-navy/5'
                    }`}>
                      <Icon size={13} strokeWidth={2} className={isActive ? 'text-gold' : 'text-navy/70'}/>
                    </span>
                    <span className="text-sm font-medium leading-tight flex-1 min-w-0">{p.title}</span>
                    {!isActive && <ChevronRight size={12} className="text-navy/30 shrink-0"/>}
                  </button>
                );
              })}
            </nav>
          </div>

          <button type="button" onClick={() => window.print()}
                  data-testid="trust-safety-print"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs text-navy/60 hover:text-navy py-2 rounded-full border border-navy/10 hover:border-navy/25 transition-smooth print:hidden">
            <Printer size={13}/> Print this page
          </button>
        </aside>

        {/* Content */}
        <article className="bg-white rounded-3xl card-soft border border-navy/5 p-5 lg:p-8 print:shadow-none print:border-0"
                 data-testid={`trust-safety-page-${active.id}`}>
          <TrustSafetyPage page={active}/>

          {/* Cross-links to related sections */}
          <RelatedLinks currentId={active.id} onPick={goTo}/>

          <p className="mt-8 text-xs text-navy/40 print:hidden">
            Last updated: February 10, 2026 · Questions? Email <a href="mailto:brotherhoodplusapp@gmail.com" className="underline decoration-navy/20 hover:decoration-navy/60">brotherhoodplusapp@gmail.com</a>
          </p>
        </article>
      </div>
    </div>
  );
}

function TrustSafetyPage({ page }) {
  const Icon = ICONS[page.icon] || FileText;
  return (
    <>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-2xl bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
          <Icon size={18} strokeWidth={2} className="text-gold"/>
        </div>
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl text-navy leading-tight">{page.title}</h2>
          <p className="text-navy/60 text-sm mt-1.5">{page.summary}</p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {page.sections.map((s, i) => (
          <Section key={s.heading || i} heading={s.heading} body={s.body} defaultOpen={i < 3}/>
        ))}
      </div>
    </>
  );
}

function Section({ heading, body, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border border-navy/8 rounded-2xl overflow-hidden print:border-navy/25">
      <button type="button"
              onClick={() => setOpen(v => !v)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-beige/40 transition-smooth print:!bg-transparent"
              aria-expanded={open}>
        <h3 className="font-heading text-base text-navy leading-tight">{heading}</h3>
        <ChevronDown size={14} className={`text-navy/50 transition-transform shrink-0 ${open ? 'rotate-180' : ''} print:hidden`}/>
      </button>
      {(open || typeof window === 'undefined') && (
        <div className="px-4 pb-4 pt-1 space-y-3 text-sm text-navy/75 leading-relaxed print:block">
          {body.map((b, i) => renderBodyPart(b, i))}
        </div>
      )}
    </div>
  );
}

function renderBodyPart(b, i) {
  if (typeof b === 'string') return <p key={i}>{b}</p>;
  if (b.list) return (
    <ul key={i} className="space-y-1.5 pl-4">
      {b.list.map((item, j) => (
        <li key={typeof item === 'string' ? item : j} className="relative">
          <span className="absolute -left-4 top-2 w-1.5 h-1.5 rounded-full bg-gold"/>
          {item}
        </li>
      ))}
    </ul>
  );
  if (b.note) return (
    <p key={i} className="rounded-xl bg-gold/8 border border-gold/25 p-3 text-navy/85 text-[13px]">
      {b.note}
    </p>
  );
  return null;
}

function RelatedLinks({ currentId, onPick }) {
  // Show 3 next pages after the current one, wrapping.
  const idx = TRUST_SAFETY_PAGES.findIndex(p => p.id === currentId);
  const related = [];
  for (let k = 1; k <= 3; k++) {
    related.push(TRUST_SAFETY_PAGES[(idx + k) % TRUST_SAFETY_PAGES.length]);
  }
  return (
    <div className="mt-8 print:hidden">
      <p className="text-[10px] uppercase tracking-[0.28em] text-navy/45 font-semibold">Continue reading</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {related.map(r => {
          const Icon = ICONS[r.icon] || FileText;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onPick(r.id)}
              data-testid={`trust-safety-related-${r.id}`}
              className="text-left rounded-2xl p-3 border border-navy/8 hover:border-navy/25 hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center shrink-0">
                  <Icon size={12} className="text-navy/70"/>
                </span>
                <p className="font-heading text-sm text-navy leading-tight">{r.title}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
